import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { ACCESS_TYPES, ACCESS_TYPE_LABELS, type UpdateProductInput } from '@aggregator/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { Table, THead, TBody, TR, TH, TD, EmptyRow } from '@/components/ui/table';
import { PageHeader, Loading, ErrorState } from '@/components/ui/states';
import { useToast } from '@/components/ui/toast';
import { useProduct, useUpdateProduct, useSetManualPrice, useDeleteProduct } from '@/api/hooks';
import { errorMessage } from '@/api/client';
import { formatUsdt } from '@/lib/utils';

export function ProductDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const product = useProduct(id);
  const update = useUpdateProduct(id);
  const setPrice = useSetManualPrice(id);
  const del = useDeleteProduct();
  const { toast } = useToast();

  const [form, setForm] = useState<UpdateProductInput | null>(null);
  const [tagsText, setTagsText] = useState('');
  const [manualEnabled, setManualEnabled] = useState(false);
  const [manualValue, setManualValue] = useState('');

  useEffect(() => {
    if (product.data) {
      const p = product.data;
      setForm({
        name: p.name,
        durationDays: p.durationDays,
        warrantyDays: p.warrantyDays,
        region: p.region,
        accessType: p.accessType,
        category: p.category,
        description: p.description,
        isActive: p.isActive,
        imageUrl: p.imageUrl,
      });
      setTagsText(p.tags.join(', '));
      setManualEnabled(p.pricingOverride != null);
      setManualValue(p.pricingOverride != null ? String(p.pricingOverride) : '');
    }
  }, [product.data]);

  if (product.isLoading || !form) return <Loading />;
  if (product.isError) return <ErrorState error={product.error} onRetry={() => product.refetch()} />;

  const p = product.data!;

  function save() {
    if (!form) return;
    update.mutate(
      { ...form, tags: tagsText.split(',').map((t) => t.trim()).filter(Boolean) },
      {
        onSuccess: () => toast({ title: 'Product saved', variant: 'success' }),
        onError: (e) => toast({ title: 'Save failed', description: errorMessage(e), variant: 'error' }),
      },
    );
  }

  function saveManualPrice() {
    const value = manualEnabled ? Number(manualValue) : null;
    if (manualEnabled && (!Number.isFinite(value) || (value ?? 0) <= 0)) {
      toast({ title: 'Enter a valid price', variant: 'error' });
      return;
    }
    setPrice.mutate(value, {
      onSuccess: () => toast({ title: value == null ? 'Manual price cleared' : 'Manual price set', variant: 'success' }),
      onError: (e) => toast({ title: 'Failed', description: errorMessage(e), variant: 'error' }),
    });
  }

  return (
    <div>
      <PageHeader
        title={p.name}
        description={`${p.slug} · ${p.durationDays}d · ${p.region} · ${p.rows.reduce((total, row) => total + (row.inStock ? (row.stockCount ?? 1) : 0), 0)} available`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => history.back()}>
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <Button
              variant="destructive"
              loading={del.isPending}
              onClick={() => {
                if (!confirm(`Delete "${p.name}" permanently? This cannot be undone.`)) return;
                del.mutate(p.id, {
                  onSuccess: () => {
                    toast({ title: 'Product deleted', variant: 'success' });
                    navigate('/products');
                  },
                  onError: (e) => toast({ title: 'Delete failed', description: errorMessage(e), variant: 'error' }),
                });
              }}
            >
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Metadata</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Name">
              <Input value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Duration (days)">
                <Input
                  type="number"
                  value={form.durationDays ?? 0}
                  onChange={(e) => setForm({ ...form, durationDays: Number(e.target.value) || 0 })}
                />
              </Field>
              <Field label="Warranty (days)">
                <Input
                  type="number"
                  value={form.warrantyDays ?? 0}
                  onChange={(e) => setForm({ ...form, warrantyDays: Number(e.target.value) || 0 })}
                />
              </Field>
              <Field label="Region">
                <Input value={form.region ?? ''} onChange={(e) => setForm({ ...form, region: e.target.value })} />
              </Field>
              <Field label="Access type">
                <Select
                  value={form.accessType ?? 'unknown'}
                  onChange={(e) => setForm({ ...form, accessType: e.target.value as UpdateProductInput['accessType'] })}
                  options={ACCESS_TYPES.map((a) => ({ value: a, label: ACCESS_TYPE_LABELS[a] }))}
                />
              </Field>
            </div>
            <Field label="Category">
              <Input value={form.category ?? ''} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </Field>
            <Field label="Tags (comma separated)">
              <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} />
            </Field>
            <Field label="Description">
              <Input value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <Field label="Icon / image URL" hint="Shown on the product's card in the bot, e.g. the service's logo.">
              <div className="flex items-center gap-3">
                {form.imageUrl ? (
                  <img src={form.imageUrl} alt="" className="h-10 w-10 rounded object-cover" />
                ) : null}
                <Input
                  value={form.imageUrl ?? ''}
                  onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                  placeholder="https://…/icon.png"
                />
              </div>
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive ?? true}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Active (visible in the bot)
            </label>
            <div className="flex justify-end">
              <Button loading={update.isPending} onClick={save}>
                Save metadata
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Manual price</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={manualEnabled} onChange={(e) => setManualEnabled(e.target.checked)} />
                Override commission tiers with a fixed selling price
              </label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  disabled={!manualEnabled}
                  value={manualValue}
                  onChange={(e) => setManualValue(e.target.value)}
                  placeholder="USDT"
                />
                <Button variant="outline" loading={setPrice.isPending} onClick={saveManualPrice}>
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Supplier price comparison</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <THead>
                  <TR>
                    <TH>Supplier</TH>
                    <TH>Cost</TH>
                    <TH>Selling</TH>
                    <TH>Available stock</TH>
                  </TR>
                </THead>
                <TBody>
                  {p.rows.length > 0 ? (
                    p.rows.map((r) => (
                      <TR key={r.supplierId} className={r.isCheapest ? 'bg-success/10' : ''}>
                        <TD>
                          <span className="font-mono text-xs">{r.externalProductId}</span>
                          {r.isCheapest ? (
                            <Badge variant="success" className="ml-2">
                              cheapest
                            </Badge>
                          ) : null}
                        </TD>
                        <TD>{formatUsdt(r.costUsdt)}</TD>
                        <TD>{formatUsdt(r.sellingPriceUsdt)}</TD>
                        <TD>
                          <Badge variant={r.inStock ? 'success' : 'secondary'}>{r.inStock ? `${r.stockCount ?? 1} available` : 'Out'}</Badge>
                        </TD>
                      </TR>
                    ))
                  ) : (
                    <EmptyRow colSpan={4}>No suppliers attached yet.</EmptyRow>
                  )}
                </TBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
